import React, { useMemo, useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import Button from '../shared/Button';
import Input from '../shared/Input';
import {
 DUE_TYPES,
 isPlatformDefaultPaymentRules,
 rulesForPaymentCalculation,
 normalizeCustomPaymentScheduleRules,
 validatePaymentScheduleRules,
 previewPaymentTermsForRules,
 createEmptyCustomRules,
} from '../../utils/paymentTerms';

const DUE_OPTIONS = [
 { value: DUE_TYPES.NOW, label: 'Due at booking (order day)' },
 { value: DUE_TYPES.SPAN_PERCENT, label: 'Along timeline (% of booking period)' },
 { value: DUE_TYPES.DAYS_BEFORE_EVENT, label: 'Days before event' },
];

const PaymentScheduleRulesEditor = ({ value, onChange, samplePrice = 370 }) => {
 const usePlatformDefault = isPlatformDefaultPaymentRules(value);
 const [expandedWindow, setExpandedWindow] = useState(null);

 const customRules = useMemo(() => {
 if (usePlatformDefault) return createEmptyCustomRules();
 return normalizeCustomPaymentScheduleRules(value);
 }, [usePlatformDefault, value]);

 const validation = useMemo(
 () => validatePaymentScheduleRules(value),
 [value]
 );

 const preview = useMemo(() => {
 const rulesConfig = usePlatformDefault
 ? { use_platform_default: true }
 : customRules;
 return previewPaymentTermsForRules(rulesConfig, samplePrice, 0);
 }, [usePlatformDefault, customRules, samplePrice]);

 const emit = (next) => {
 onChange(next);
 };

 const setUsePlatformDefault = (checked) => {
 if (checked) {
 emit({ use_platform_default: true });
 setExpandedWindow(null);
 return;
 }
 if (!usePlatformDefault && value?.windows?.length) {
 emit({ use_platform_default: false, windows: value.windows });
 } else {
 emit(createEmptyCustomRules());
 setExpandedWindow(0);
 }
 };

 const updateWindow = (index, patch) => {
 const windows = customRules.windows.map((w, i) =>
 i === index ? { ...w, ...patch } : w
 );
 emit({ use_platform_default: false, windows });
 };

 const updateInstallment = (windowIndex, instIndex, patch) => {
 const windows = customRules.windows.map((w, wi) => {
 if (wi !== windowIndex) return w;
 const installments = w.installments.map((inst, ii) =>
 ii === instIndex ? { ...inst, ...patch } : inst
 );
 return { ...w, installments };
 });
 emit({ use_platform_default: false, windows });
 };

 const addWindow = () => {
 const windows = [
 ...customRules.windows,
 {
 id: `window-${Date.now()}`,
 label: 'New booking window',
 min_days: 30,
 max_days: 150,
 description: '',
 installments: [
 { label: 'Booking deposit', percentage: 50, due: { type: DUE_TYPES.NOW } },
 {
 label: 'Balance',
 percentage: 50,
 due: { type: DUE_TYPES.SPAN_PERCENT, percent: 85 },
 },
 ],
 },
 ];
 emit({ use_platform_default: false, windows });
 setExpandedWindow(windows.length - 1);
 };

 const removeWindow = (index) => {
 const windows = customRules.windows.filter((_, i) => i !== index);
 emit({ use_platform_default: false, windows });
 };

 const addInstallment = (windowIndex) => {
 const windows = customRules.windows.map((w, wi) => {
 if (wi !== windowIndex) return w;
 return {
 ...w,
 installments: [
 ...w.installments,
 { label: 'Payment', percentage: 0, due: { type: DUE_TYPES.NOW } },
 ],
 };
 });
 emit({ use_platform_default: false, windows });
 };

 const removeInstallment = (windowIndex, instIndex) => {
 const windows = customRules.windows.map((w, wi) => {
 if (wi !== windowIndex) return w;
 return {
 ...w,
 installments: w.installments.filter((_, ii) => ii !== instIndex),
 };
 });
 emit({ use_platform_default: false, windows });
 };

 const formatPreviewAmount = (payment) => {
 const amount =
 payment.subtotalAmount ??
 Math.round(((samplePrice * payment.percentage) / 100) * 100) / 100;
 return amount.toFixed(2);
 };

 return (
 <div className="space-y-4">
 <label className="flex items-start gap-3 cursor-pointer">
 <input
 type="checkbox"
 checked={usePlatformDefault}
 onChange={(e) => setUsePlatformDefault(e.target.checked)}
 className="mt-1 rounded border-gray-300 text-ink focus:ring-ink"
 />
 <span>
 <span className="text-sm font-medium text-gray-800">
 Use platform default payment schedule
 </span>
 <p className="text-xs text-gray-500 mt-0.5">
 Standard rules based on how far the event is from the booking date. Uncheck to set your
 own deposit and installment windows.
 </p>
 </span>
 </label>

 {!usePlatformDefault && (
 <div className="space-y-3">
 {!validation.valid && (
 <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900">
 <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
 <ul className="list-disc list-inside space-y-0.5">
 {validation.errors.map((err) => (
 <li key={err}>{err}</li>
 ))}
 </ul>
 </div>
 )}

 {customRules.windows.map((window, wi) => {
 const totalPct = window.installments.reduce((s, i) => s + i.percentage, 0);
 const isOpen = expandedWindow === wi;

 return (
 <div
 key={window.id || wi}
 className="border border-gray-200 rounded-lg bg-surface overflow-hidden"
 >
 <button
 type="button"
 className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-surface-muted"
 onClick={() => setExpandedWindow(isOpen ? null : wi)}
 >
 <span className="font-medium text-sm text-gray-900">
 {window.label || `Window ${wi + 1}`}
 <span className="ml-2 text-xs font-normal text-gray-500">
 ({window.min_days}–{window.max_days ?? '∞'} days out)
 </span>
 </span>
 {isOpen ? (
 <ChevronUp className="w-4 h-4 text-gray-500" />
 ) : (
 <ChevronDown className="w-4 h-4 text-gray-500" />
 )}
 </button>

 {isOpen && (
 <div className="px-4 pb-4 space-y-3 border-t border-gray-100">
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
 <div>
 <label className="block text-xs font-medium text-gray-600 mb-1">
 Window label
 </label>
 <Input
 value={window.label}
 onChange={(e) => updateWindow(wi, { label: e.target.value })}
 placeholder="e.g. 1–5 months before event"
 />
 </div>
 <div className="grid grid-cols-2 gap-2">
 <div>
 <label className="block text-xs font-medium text-gray-600 mb-1">
 Min days out
 </label>
 <Input
 type="number"
 min={0}
 value={window.min_days}
 onChange={(e) =>
 updateWindow(wi, { min_days: parseInt(e.target.value, 10) || 0 })
 }
 />
 </div>
 <div>
 <label className="block text-xs font-medium text-gray-600 mb-1">
 Max days (empty = no max)
 </label>
 <Input
 type="number"
 min={0}
 value={window.max_days ?? ''}
 onChange={(e) =>
 updateWindow(wi, {
 max_days:
 e.target.value === ''
 ? null
 : parseInt(e.target.value, 10) || 0,
 })
 }
 placeholder="No limit"
 />
 </div>
 </div>
 </div>

 <div>
 <label className="block text-xs font-medium text-gray-600 mb-1">
 Client-facing description
 </label>
 <textarea
 value={window.description}
 onChange={(e) => updateWindow(wi, { description: e.target.value })}
 rows={2}
 className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-ink focus:border-ink"
 placeholder="Explain when payments are due..."
 />
 </div>

 <div className="space-y-2">
 <div className="flex items-center justify-between">
 <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
 Installments
 </span>
 <span
 className={`text-xs ${ Math.abs(totalPct - 100) < 0.01 ? 'text-green-600' : 'text-amber-600' }`}
 >
 Total: {totalPct}%
 </span>
 </div>

 {window.installments.map((inst, ii) => (
 <div
 key={`${wi}-${ii}`}
 className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end p-2 bg-surface-muted rounded-md"
 >
 <div className="sm:col-span-3">
 <label className="block text-xs text-gray-500 mb-1">Label</label>
 <Input
 value={inst.label}
 onChange={(e) =>
 updateInstallment(wi, ii, { label: e.target.value })
 }
 />
 </div>
 <div className="sm:col-span-2">
 <label className="block text-xs text-gray-500 mb-1">%</label>
 <Input
 type="number"
 min={0}
 max={100}
 step={1}
 value={inst.percentage}
 onChange={(e) =>
 updateInstallment(wi, ii, {
 percentage: parseFloat(e.target.value) || 0,
 })
 }
 />
 </div>
 <div className="sm:col-span-3">
 <label className="block text-xs text-gray-500 mb-1">Due</label>
 <select
 value={inst.due?.type || DUE_TYPES.NOW}
 onChange={(e) => {
 const type = e.target.value;
 let due = { type: DUE_TYPES.NOW };
 if (type === DUE_TYPES.DAYS_BEFORE_EVENT) {
 due = { type, days: inst.due?.days || 14 };
 } else if (type === DUE_TYPES.SPAN_PERCENT) {
 due = {
 type,
 percent:
 inst.due?.percent != null ? inst.due.percent : 50,
 };
 }
 updateInstallment(wi, ii, { due });
 }}
 className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md"
 >
 {DUE_OPTIONS.map((opt) => (
 <option key={opt.value} value={opt.value}>
 {opt.label}
 </option>
 ))}
 </select>
 </div>
 {inst.due?.type === DUE_TYPES.SPAN_PERCENT && (
 <div className="sm:col-span-2">
 <label className="block text-xs text-gray-500 mb-1">
 % into period
 </label>
 <Input
 type="number"
 min={0}
 max={100}
 value={inst.due.percent ?? 50}
 onChange={(e) =>
 updateInstallment(wi, ii, {
 due: {
 type: DUE_TYPES.SPAN_PERCENT,
 percent: parseFloat(e.target.value) || 0,
 },
 })
 }
 />
 </div>
 )}
 {inst.due?.type === DUE_TYPES.DAYS_BEFORE_EVENT && (
 <div className="sm:col-span-2">
 <label className="block text-xs text-gray-500 mb-1">
 Days before
 </label>
 <Input
 type="number"
 min={0}
 value={inst.due.days ?? 14}
 onChange={(e) =>
 updateInstallment(wi, ii, {
 due: {
 type: DUE_TYPES.DAYS_BEFORE_EVENT,
 days: parseInt(e.target.value, 10) || 0,
 },
 })
 }
 />
 </div>
 )}
 <div className="sm:col-span-2 flex justify-end">
 <button
 type="button"
 onClick={() => removeInstallment(wi, ii)}
 className="p-2 text-red-500 hover:bg-red-50 rounded-md"
 title="Remove installment"
 disabled={window.installments.length <= 1}
 >
 <Trash2 className="w-4 h-4" />
 </button>
 </div>
 </div>
 ))}

 <Button
 type="button"
 variant="outline"
 size="sm"
 icon={Plus}
 onClick={() => addInstallment(wi)}
 >
 Add installment
 </Button>
 </div>

 <div className="flex justify-end">
 <Button
 type="button"
 variant="outline"
 size="sm"
 icon={Trash2}
 onClick={() => removeWindow(wi)}
 className="text-red-600 border-red-200"
 disabled={customRules.windows.length <= 1}
 >
 Remove window
 </Button>
 </div>
 </div>
 )}
 </div>
 );
 })}

 <Button type="button" variant="outline" size="sm" icon={Plus} onClick={addWindow}>
 Add booking window
 </Button>
 </div>
 )}

 <div className="rounded-lg border border-border bg-surface-muted p-4">
 <p className="text-xs font-semibold text-ink mb-1">
 Preview (your ${Number(samplePrice).toFixed(2)} package price)
 </p>
 <p className="text-xs text-ink mb-2">
 Dates and amounts follow the client&apos;s event date vs today. Use &quot;Due at booking&quot;
 for the deposit on order day; use timeline % to spread the rest between booking and the event.
 Platform fees are added at client checkout only.
 </p>
 <div className="space-y-3">
 {preview.map((row) => (
 <div key={row.label} className="text-xs text-ink">
 <p className="font-medium">{row.label}</p>
 {row.terms ? (
 <ul className="mt-1 space-y-0.5 text-ink">
 <li>{row.terms.matchedWindowLabel}</li>
 {row.terms.schedule.map((p) => (
 <li key={p.label}>
 {p.label} ({p.percentage}%): ${formatPreviewAmount(p)} — {p.dueDate}
 </li>
 ))}
 <li className="font-semibold text-ink">
 Booking deposit (order day): $
 {(row.terms.bookingDepositAmount ?? 0).toFixed(2)}
 </li>
 {row.terms.amountDueNow > (row.terms.bookingDepositAmount ?? 0) + 0.01 && (
 <li className="text-amber-800">
 Total due at checkout (incl. overdue milestones): $
 {row.terms.amountDueNow.toFixed(2)}
 </li>
 )}
 </ul>
 ) : (
 <p className="text-amber-700">No matching window for this date range.</p>
 )}
 </div>
 ))}
 </div>
 </div>
 </div>
 );
};

export default PaymentScheduleRulesEditor;
